import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DeleteOutlined,
  DownloadOutlined,
  ImportOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, DatePicker, Drawer, Form, Input, message, Modal, Radio, Select, Space, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { getPetBreedListApi, PetBreedItem } from '@/api/petManage/petBreed';
import { getPetTagListApi, PetTagItem } from '@/api/petManage/petTag';
import {
  getLifeStageByPetTypeApi,
  getPetTypeListApi,
  PetLifeStageItem,
  PetTypeDictItem,
} from '@/api/petManage/petType';
import {
  addOrUpdateGoodsRecommendTaskAndGoodsApi,
  batchDeleteGoodsRecommendApi,
  exportGoodsRecommendDataApi,
  getBoughtDictApi,
  getGoodsRecommendDetailListApi,
  getGoodsRecommendListApi,
  getGoodsRecommendImportResultApi,
  getGoodsRecommendTaskDetailApi,
  getGoodsRecommendTaskListApi,
  importGoodsRecommendDataApi,
  downloadGoodsRecommendTemplateApi,
} from '@/api/pointCenter/goodsRecommend';
import { GOODS_RECOMMEND_KEY } from '@/router/Permissions';
import { checkPermissions } from '@/utils/utils';
import { getRecommendLogicOptions } from '../constants';
import RuleEditorModal from './RuleEditorModal';

type TaskDrawerMode = 'create' | 'edit' | 'view';

type TaskDrawerProps = {
  open: boolean;
  mode: TaskDrawerMode;
  shopId: number | string;
  shopType?: number | null;
  platformType?: string | null;
  taskId?: number | string | null;
  onClose: () => void;
  onSuccess: () => void;
};

const { RangePicker } = DatePicker;

const TAG_PAGE_SIZE = 1000;
const IMPORT_RESULT_POLL_INTERVAL = 2000;
const IMPORT_RESULT_POLL_TIMES = 30;

const TaskDrawer: React.FC<TaskDrawerProps> = ({
  open,
  mode,
  shopId,
  shopType,
  platformType,
  taskId,
  onClose,
  onSuccess,
}) => {
  const [taskForm] = Form.useForm();
  const [queryForm] = Form.useForm();
  const [taskLoading, setTaskLoading] = useState(false);
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [taskStep, setTaskStep] = useState<1 | 2>(1);
  const [currentTaskStatus, setCurrentTaskStatus] = useState<number | null>(null);
  const [resolvedTaskId, setResolvedTaskId] = useState<number | string | null>(taskId ?? null);
  const [ruleListLoading, setRuleListLoading] = useState(false);
  const [ruleDataSource, setRuleDataSource] = useState<any[]>([]);
  const [ruleDetailDataSource, setRuleDetailDataSource] = useState<any[]>([]);
  const [ruleCurrentPage, setRuleCurrentPage] = useState(1);
  const [rulePageSize, setRulePageSize] = useState(10);
  const [selectedRuleIds, setSelectedRuleIds] = useState<Array<number | string>>([]);
  const [boughtDict, setBoughtDict] = useState<any[]>([]);
  const [petTypeOptions, setPetTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [filterAgeStageOptions, setFilterAgeStageOptions] = useState<{ label: string; value: string }[]>([]);
  const [filterBreedOptions, setFilterBreedOptions] = useState<{ label: string; value: string }[]>([]);
  const [tagOptions, setTagOptions] = useState<{ label: string; value: string }[]>([]);
  const [ageStageLoading, setAgeStageLoading] = useState(false);
  const [breedLoading, setBreedLoading] = useState(false);
  const [tagLoading, setTagLoading] = useState(false);
  const [tagPageNum, setTagPageNum] = useState(1);
  const [tagTotal, setTagTotal] = useState(0);
  const [importLoading, setImportLoading] = useState(false);
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [ruleModalMode, setRuleModalMode] = useState<'create' | 'edit' | 'view'>('create');
  const [currentRuleId, setCurrentRuleId] = useState<number | string | null>(null);
  const [draftRules, setDraftRules] = useState<any[]>([]);
  const [draftRuleQuery, setDraftRuleQuery] = useState<Record<string, any>>({});
  const [pendingDeletedRuleIds, setPendingDeletedRuleIds] = useState<Array<number | string>>([]);
  const [pendingImportedRules, setPendingImportedRules] = useState<any[]>([]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const draftRuleSeedRef = useRef(0);
  const isView = mode === 'view';
  const disableTaskEffectTime = isView || (mode === 'edit' && currentTaskStatus === 1);
  const logicOptions = useMemo(() => getRecommendLogicOptions(shopType), [shopType]);
  const selectedLogicValues = Form.useWatch('recommendOrderList', taskForm) || [];

  const drawerTitle = useMemo(() => {
    if (mode === 'edit') return '编辑商品推荐任务';
    if (mode === 'view') return '查看商品推荐任务';
    return '新建商品推荐任务';
  }, [mode]);

  const buildDefaultTaskValues = () => ({
    taskName: '',
    effectMode: 'immediate',
    scheduledTime: null,
    recommendOrderList: logicOptions.map(() => undefined),
  });

  const resetRuleFilters = () => {
    queryForm.resetFields();
    setFilterAgeStageOptions([]);
    setFilterBreedOptions([]);
    setTagOptions([]);
    setTagPageNum(1);
    setTagTotal(0);
  };

  const fetchBoughtDict = async () => {
    try {
      const res = await getBoughtDictApi();
      if (res?.code === 200) {
        setBoughtDict(res.data || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPetTypes = async () => {
    try {
      const res = await getPetTypeListApi();
      const list: PetTypeDictItem[] = Array.isArray(res?.data) ? res.data : [];
      if (res?.code === 200) {
        setPetTypeOptions(
          list.map(item => ({
            label: item.dictName,
            value: String(item.dictCode ?? ''),
          })),
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAgeStages = async (petType?: string | number) => {
    if (petType === undefined || petType === null || petType === '') {
      setFilterAgeStageOptions([]);
      return;
    }
    setAgeStageLoading(true);
    try {
      const res = await getLifeStageByPetTypeApi(petType);
      const list: PetLifeStageItem[] = Array.isArray(res?.data) ? res.data : [];
      if (res?.code === 200) {
        setFilterAgeStageOptions(
          list.map(item => ({
            label: item.stageName,
            value: String(item.id ?? ''),
          })),
        );
      } else {
        setFilterAgeStageOptions([]);
      }
    } catch (error) {
      console.error(error);
      setFilterAgeStageOptions([]);
    } finally {
      setAgeStageLoading(false);
    }
  };

  const fetchBreeds = async (petType?: string | number) => {
    if (petType === undefined || petType === null || petType === '') {
      setFilterBreedOptions([]);
      return;
    }
    setBreedLoading(true);
    try {
      const res = await getPetBreedListApi({ petType });
      const list: PetBreedItem[] = Array.isArray(res?.data) ? res.data : [];
      if (res?.code === 200) {
        setFilterBreedOptions(
          list.map(item => ({
            label: item.breedName || '',
            value: String(item.id ?? ''),
          })),
        );
      } else {
        setFilterBreedOptions([]);
      }
    } catch (error) {
      console.error(error);
      setFilterBreedOptions([]);
    } finally {
      setBreedLoading(false);
    }
  };

  const fetchTags = async (page = 1) => {
    setTagLoading(true);
    try {
      const res = await getPetTagListApi({ pageNum: page, pageSize: TAG_PAGE_SIZE });
      const rows: PetTagItem[] = Array.isArray(res?.rows) ? res.rows : [];
      const total = typeof res?.total === 'number' ? res.total : 0;
      const options = rows.map(item => ({
        label: item.tagName || '',
        value: String(item.id ?? ''),
      }));
      setTagOptions(prev => (page === 1 ? options : [...prev, ...options]));
      setTagPageNum(page);
      setTagTotal(total);
    } catch (error) {
      console.error(error);
    } finally {
      setTagLoading(false);
    }
  };

  const normalizeRecommendOrderList = (recommendOrderList?: string[]) => {
    const baseValues = logicOptions.map(item => item.value);
    const result: string[] = [];
    (recommendOrderList || []).forEach(item => {
      if (item && baseValues.includes(item) && !result.includes(item)) {
        result.push(item);
      }
    });
    return result;
  };

  const getTaskPayload = async () => {
    const values = taskForm.getFieldsValue(true);
    const normalizedTaskName = values.taskName?.trim();
    const recommendOrderList = normalizeRecommendOrderList(values.recommendOrderList);

    if (!normalizedTaskName) {
      throw new Error('请输入任务名称');
    }
    if (normalizedTaskName.length > 50) {
      throw new Error('任务名称不超过50个字符');
    }
    if (!values.effectMode) {
      throw new Error('请选择任务生效时间');
    }
    if (values.effectMode === 'scheduled' && !values.scheduledTime) {
      throw new Error('请选择生效时间');
    }
    if (recommendOrderList.length !== logicOptions.length) {
      throw new Error('请选择完整的商品推荐逻辑');
    }

    return {
      id: mode === 'create' ? undefined : resolvedTaskId || taskId,
      shopId,
      taskName: normalizedTaskName,
      status: values.effectMode === 'immediate' ? 1 : 0,
      startTime:
        values.effectMode === 'scheduled' && values.scheduledTime
          ? values.scheduledTime.format('YYYY-MM-DD HH:mm:ss')
          : dayjs().format('YYYY-MM-DD HH:mm:ss'),
      endTime: undefined,
      recommendOrderList,
    };
  };

  const isCreateDraftMode = mode === 'create' && !resolvedTaskId;

  const getImportGroupKey = (item: any) =>
    JSON.stringify([
      item?.ruleId ?? '',
      item?.petType ?? '',
      item?.ageStage ?? '',
      item?.petBreed ?? '',
      item?.tag ?? '',
    ]);

  const buildImportedGoodsIdList = (rows: any[] = []) =>
    Array.from(
      new Map(
        rows
          .map(item => item?.id)
          .filter(id => id !== undefined && id !== null && id !== '')
          .map(id => [String(id), { id }]),
      ).values(),
    );

  const buildImportedRawGoodsPayload = (rules: any[] = []) =>
    rules.flatMap(rule => (Array.isArray(rule?.__importPayloadRows) ? rule.__importPayloadRows : []));

  const isImportedRule = (rule: any) =>
    Boolean(rule?.__isImport) || (Array.isArray(rule?.__importPayloadRows) && rule.__importPayloadRows.length > 0);

  const cleanGoodsForSubmit = (goods: any) => {
    const { __rowId, __localRuleId, ...rest } = goods || {};
    return rest;
  };

  const buildRuleDetailGoodsPayload = (rules: any[] = [], deletedIds: Array<number | string> = []) => {
    const deletedIdSet = new Set(deletedIds.map(id => String(id)));
    return rules
      .map(rule => {
        const {
          __localRuleId,
          __isLocalManual,
          __draftId,
          __isImport,
          __importGroupKey,
          __importPayloadRows,
          _display,
          ...restRule
        } = rule || {};
        const purchasedGoodsList = Array.isArray(rule?.purchasedGoodsList)
          ? rule.purchasedGoodsList.filter((item: any) => !deletedIdSet.has(String(item?.id)))
          : [];
        const unPurchasedGoodsList = Array.isArray(rule?.unPurchasedGoodsList)
          ? rule.unPurchasedGoodsList.filter((item: any) => !deletedIdSet.has(String(item?.id)))
          : [];
        return {
          ...restRule,
          purchasedGoodsList: purchasedGoodsList.map(cleanGoodsForSubmit),
          unPurchasedGoodsList: unPurchasedGoodsList.map(cleanGoodsForSubmit),
        };
      })
      .filter(rule => rule.purchasedGoodsList.length > 0 || rule.unPurchasedGoodsList.length > 0);
  };

  const getRuleGoodsIds = (rule: any) =>
    [
      ...(Array.isArray(rule?.purchasedGoodsList) ? rule.purchasedGoodsList : []),
      ...(Array.isArray(rule?.unPurchasedGoodsList) ? rule.unPurchasedGoodsList : []),
    ]
      .map(item => item?.id ?? item?.__rowId)
      .filter(id => id !== undefined && id !== null && id !== '')
      .map(id => String(id));

  const buildRuleRowsFromDetail = (rule: any) => {
    const ruleId = rule?.ruleId || rule?.__localRuleId || currentRuleId || `local-${Date.now()}`;
    const commonFields = {
      ruleId,
      __localRuleId: rule?.__localRuleId,
      petType: rule?._display?.petTypeLabel || rule?.petType || '-',
      petTypeValue: rule?.petType,
      ageStage: rule?._display?.ageStageLabel || rule?.ageStage || '-',
      ageStageValue: rule?.ageStage,
      petBreed: rule?._display?.petBreedLabel || rule?.petBreed || '-',
      petBreedValue: rule?.petBreed,
      tag: rule?._display?.tagLabel || rule?.tag || '-',
      tagValue: rule?.tag,
    };
    const purchasedRows = Array.isArray(rule?.purchasedGoodsList)
      ? rule.purchasedGoodsList.map((item: any, index: number) => ({
          ...item,
          ...commonFields,
          __rowId: item?.id ?? item?.__rowId ?? `${ruleId}-p-${index}`,
          id: item?.id ?? item?.__rowId ?? `${ruleId}-p-${index}`,
          purchasedFlag: 1,
          priorityWeight: item?.priorityWeight ?? index + 1,
        }))
      : [];
    const unPurchasedRows = Array.isArray(rule?.unPurchasedGoodsList)
      ? rule.unPurchasedGoodsList.map((item: any, index: number) => ({
          ...item,
          ...commonFields,
          __rowId: item?.id ?? item?.__rowId ?? `${ruleId}-u-${index}`,
          id: item?.id ?? item?.__rowId ?? `${ruleId}-u-${index}`,
          purchasedFlag: 0,
          priorityWeight: item?.priorityWeight ?? index + 1,
        }))
      : [];
    return [...purchasedRows, ...unPurchasedRows];
  };

  const handleSaveLocalTaskRule = (goodsPayload: any) => {
    const localRuleId =
      goodsPayload?.ruleId ||
      (ruleModalMode === 'edit' ? currentRuleId : undefined) ||
      `local-rule-${Date.now()}-${++draftRuleSeedRef.current}`;
    const nextRule = {
      ...goodsPayload,
      ruleId: goodsPayload?.ruleId || '',
      __localRuleId: localRuleId,
      __isLocalManual: true,
    };
    setRuleDetailDataSource(prev => {
      const matchedRule = prev.find(rule => {
        if (goodsPayload?.ruleId && rule?.ruleId && String(rule.ruleId) === String(goodsPayload.ruleId)) return true;
        return getRuleGoodsIds(rule).includes(String(currentRuleId));
      });
      if (matchedRule) {
        return prev.map(rule => (rule === matchedRule ? nextRule : rule));
      }
      return [nextRule, ...prev];
    });
    setRuleDataSource(prev => {
      const previousIds = new Set<string>();
      ruleDetailDataSource.forEach(rule => {
        const sameRule =
          (goodsPayload?.ruleId && rule?.ruleId && String(rule.ruleId) === String(goodsPayload.ruleId)) ||
          getRuleGoodsIds(rule).includes(String(currentRuleId));
        if (sameRule) {
          getRuleGoodsIds(rule).forEach(id => previousIds.add(id));
        }
      });
      previousIds.add(String(currentRuleId));
      const nextRows = buildRuleRowsFromDetail(nextRule);
      const remainedRows = prev.filter(row => !previousIds.has(String(row?.id ?? row?.__rowId)));
      return ruleModalMode === 'edit' ? [...nextRows, ...remainedRows] : [...nextRows, ...prev];
    });
    setSelectedRuleIds([]);
    setRuleCurrentPage(1);
  };

  const buildGoodsPayload = (
    rule: any,
    nextTaskId?: number | string,
    options?: {
      importIdsOnly?: boolean;
    },
  ) => {
    const { __draftId, __isImport, _display, shopId: _, taskId: __, ...rest } = rule || {};
    if (options?.importIdsOnly) {
      return {
        ...rest,
        purchasedGoodsList: buildImportedGoodsIdList(rule?.purchasedGoodsList),
        unPurchasedGoodsList: buildImportedGoodsIdList(rule?.unPurchasedGoodsList),
        shopId,
        taskId: nextTaskId,
      };
    }
    return {
      ...rest,
      shopId,
      taskId: nextTaskId,
    };
  };

  const buildDraftRows = (rules: any[]) =>
    rules.flatMap(rule => {
      const draftId = rule.__draftId;
      const purchasedRows = Array.isArray(rule.purchasedGoodsList)
        ? rule.purchasedGoodsList.map((item: any, index: number) => ({
            __rowId: `${draftId}-p-${index}`,
            __draftId: draftId,
            petType: rule._display?.petTypeLabel || rule.petType || '-',
            petTypeValue: rule.petType,
            ageStage: rule._display?.ageStageLabel || rule.ageStage || '-',
            ageStageValue: rule.ageStage,
            petBreed: rule._display?.petBreedLabel || rule.petBreed || '-',
            petBreedValue: rule.petBreed,
            tag: rule._display?.tagLabel || rule.tag || '-',
            tagValue: rule.tag,
            purchasedFlag: 1,
            skuId: item?.skuId,
            productName: item?.productName,
            sellingPoint: item?.sellingPoint,
            priorityWeight: item?.priorityWeight ?? index + 1,
          }))
        : [];
      const unPurchasedRows = Array.isArray(rule.unPurchasedGoodsList)
        ? rule.unPurchasedGoodsList.map((item: any, index: number) => ({
            __rowId: `${draftId}-u-${index}`,
            __draftId: draftId,
            petType: rule._display?.petTypeLabel || rule.petType || '-',
            petTypeValue: rule.petType,
            ageStage: rule._display?.ageStageLabel || rule.ageStage || '-',
            ageStageValue: rule.ageStage,
            petBreed: rule._display?.petBreedLabel || rule.petBreed || '-',
            petBreedValue: rule.petBreed,
            tag: rule._display?.tagLabel || rule.tag || '-',
            tagValue: rule.tag,
            purchasedFlag: 0,
            skuId: item?.skuId,
            productName: item?.productName,
            sellingPoint: item?.sellingPoint,
            priorityWeight: item?.priorityWeight ?? index + 1,
          }))
        : [];
      return [...purchasedRows, ...unPurchasedRows];
    });

  const normalizeImportedRuleGoods = (rows: any[] = []) =>
    [...rows]
      .sort((a, b) => Number(a?.priorityWeight || 0) - Number(b?.priorityWeight || 0))
      .map(item => {
        const { petType, ageStage, petBreed, tag, purchasedFlag, createTime, updateTime, status, ...rest } = item || {};
        return {
          ...rest,
          priorityWeight: Number(item?.priorityWeight || 0),
        };
      });

  const dedupeImportedGoodsList = (rows: any[] = []) => {
    const goodsMap = new Map<string, any>();
    normalizeImportedRuleGoods(rows).forEach(item => {
      const key = JSON.stringify([
        item?.id ?? '',
        item?.itemId ?? '',
        item?.skuId ?? '',
        item?.productName ?? '',
        item?.priorityWeight ?? '',
      ]);
      if (!goodsMap.has(key)) {
        goodsMap.set(key, item);
      }
    });
    return Array.from(goodsMap.values());
  };

  const buildImportedLabelMaps = async (rows: any[] = []) => {
    const petTypeLabelMap = petTypeOptions.reduce<Record<string, string>>((acc, item) => {
      acc[String(item.value)] = item.label;
      return acc;
    }, {});
    const tagLabelMap = tagOptions.reduce<Record<string, string>>((acc, item) => {
      acc[String(item.value)] = item.label;
      return acc;
    }, {});
    const ageStageLabelMap: Record<string, string> = {};
    const breedLabelMap: Record<string, string> = {};
    const petTypes = Array.from(new Set(rows.map(item => String(item?.petType || '')).filter(Boolean)));
    await Promise.all(
      petTypes.map(async petType => {
        try {
          const [ageStageRes, breedRes] = await Promise.all([
            getLifeStageByPetTypeApi(petType),
            getPetBreedListApi({ petType }),
          ]);
          const ageStageList: PetLifeStageItem[] = Array.isArray(ageStageRes?.data) ? ageStageRes.data : [];
          const breedList: PetBreedItem[] = Array.isArray(breedRes?.data) ? breedRes.data : [];
          ageStageList.forEach(item => {
            ageStageLabelMap[String(item.id ?? '')] = item.stageName || String(item.id ?? '');
          });
          breedList.forEach(item => {
            breedLabelMap[String(item.id ?? '')] = item.breedName || String(item.id ?? '');
          });
        } catch (error) {
          console.error(error);
        }
      }),
    );
    return {
      petTypeLabelMap,
      ageStageLabelMap,
      breedLabelMap,
      tagLabelMap,
    };
  };

  const mapImportedRowsToDraftRules = async (rows: any[] = []) => {
    const { petTypeLabelMap, ageStageLabelMap, breedLabelMap, tagLabelMap } = await buildImportedLabelMaps(rows);
    const grouped = new Map<string, any>();
    rows.forEach(item => {
      const groupKey = getImportGroupKey(item);
      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, {
          __draftId: `draft-import-${Date.now()}-${++draftRuleSeedRef.current}`,
          __isImport: true,
          __importGroupKey: groupKey,
          __importPayloadRows: [],
          ruleId: item?.ruleId || '',
          petType: item?.petType,
          petBreed: item?.petBreed,
          ageStage: item?.ageStage,
          tag: item?.tag,
          purchasedGoodsList: [],
          unPurchasedGoodsList: [],
          _display: {
            petTypeLabel: petTypeLabelMap[String(item?.petType ?? '')] || String(item?.petType ?? '-'),
            ageStageLabel: ageStageLabelMap[String(item?.ageStage ?? '')] || String(item?.ageStage ?? '-'),
            petBreedLabel: breedLabelMap[String(item?.petBreed ?? '')] || String(item?.petBreed ?? '-'),
            tagLabel:
              item?.tag === undefined || item?.tag === null || item?.tag === ''
                ? '-'
                : tagLabelMap[String(item.tag)] || String(item.tag),
          },
        });
      }
      const currentGroup = grouped.get(groupKey);
      currentGroup.__importPayloadRows.push({
        ...item,
        purchasedGoodsList: Array.isArray(item?.purchasedGoodsList)
          ? item.purchasedGoodsList.map((goods: any) => ({ ...goods }))
          : [],
        unPurchasedGoodsList: Array.isArray(item?.unPurchasedGoodsList)
          ? item.unPurchasedGoodsList.map((goods: any) => ({ ...goods }))
          : [],
      });
      if (Array.isArray(item?.purchasedGoodsList) || Array.isArray(item?.unPurchasedGoodsList)) {
        currentGroup.purchasedGoodsList = dedupeImportedGoodsList([
          ...currentGroup.purchasedGoodsList,
          ...(item?.purchasedGoodsList || []),
        ]);
        currentGroup.unPurchasedGoodsList = dedupeImportedGoodsList([
          ...currentGroup.unPurchasedGoodsList,
          ...(item?.unPurchasedGoodsList || []),
        ]);
        return;
      }
      const targetList =
        Number(item?.purchasedFlag) === 1 ? currentGroup.purchasedGoodsList : currentGroup.unPurchasedGoodsList;
      targetList.push(item);
    });
    return Array.from(grouped.values()).map(item => ({
      ...item,
      purchasedGoodsList: dedupeImportedGoodsList(item.purchasedGoodsList),
      unPurchasedGoodsList: dedupeImportedGoodsList(item.unPurchasedGoodsList),
    }));
  };

  const buildImportPayloadRowsFromRule = (rule: any) => [
    {
      shopId,
      taskId: rule?.taskId ?? null,
      ruleId: rule?.ruleId || '',
      petType: rule?.petType,
      petBreed: rule?.petBreed,
      ageStage: rule?.ageStage,
      tag: rule?.tag || '',
      purchasedGoodsList: Array.isArray(rule?.purchasedGoodsList)
        ? rule.purchasedGoodsList.map((item: any) => ({ ...item }))
        : [],
      unPurchasedGoodsList: Array.isArray(rule?.unPurchasedGoodsList)
        ? rule.unPurchasedGoodsList.map((item: any) => ({ ...item }))
        : [],
    },
  ];

  const currentDraftRule = useMemo(() => {
    if (!isCreateDraftMode) return undefined;
    return draftRules.find(rule => String(rule.__draftId) === String(currentRuleId));
  }, [currentRuleId, draftRules, isCreateDraftMode]);

  const currentPendingImportedRule = useMemo(() => {
    if (mode !== 'edit') return undefined;
    return pendingImportedRules.find(rule => String(rule.__draftId) === String(currentRuleId));
  }, [currentRuleId, mode, pendingImportedRules]);

  const currentLocalManualRule = useMemo(() => {
    if (mode !== 'edit') return undefined;
    return ruleDetailDataSource.find(rule => String(rule.__localRuleId) === String(currentRuleId));
  }, [currentRuleId, mode, ruleDetailDataSource]);

  const draftRuleRows = useMemo(() => buildDraftRows(draftRules), [draftRules]);
  const pendingImportedRuleRows = useMemo(() => buildDraftRows(pendingImportedRules), [pendingImportedRules]);

  const filteredDraftRuleRows = useMemo(() => {
    if (!isCreateDraftMode) return [];
    return draftRuleRows.filter(row => {
      if (draftRuleQuery.petType && String(row.petTypeValue) !== String(draftRuleQuery.petType)) return false;
      if (draftRuleQuery.ageStage && String(row.ageStageValue) !== String(draftRuleQuery.ageStage)) return false;
      if (draftRuleQuery.petBreed && String(row.petBreedValue) !== String(draftRuleQuery.petBreed)) return false;
      if (draftRuleQuery.tag && String(row.tagValue) !== String(draftRuleQuery.tag)) return false;
      if (
        draftRuleQuery.purchasedFlag !== undefined &&
        draftRuleQuery.purchasedFlag !== null &&
        draftRuleQuery.purchasedFlag !== '' &&
        Number(row.purchasedFlag) !== Number(draftRuleQuery.purchasedFlag)
      ) {
        return false;
      }
      if (draftRuleQuery.skuId && !String(row.skuId || '').includes(String(draftRuleQuery.skuId))) return false;
      return true;
    });
  }, [draftRuleQuery, draftRuleRows, isCreateDraftMode]);

  const stepRuleDataSource = useMemo(
    () =>
      isCreateDraftMode
        ? filteredDraftRuleRows
        : [...pendingImportedRuleRows, ...ruleDataSource.filter(item => !pendingDeletedRuleIds.includes(item.id))],
    [filteredDraftRuleRows, isCreateDraftMode, pendingDeletedRuleIds, pendingImportedRuleRows, ruleDataSource],
  );
  const pagedRuleDataSource = useMemo(() => {
    const start = (ruleCurrentPage - 1) * rulePageSize;
    return stepRuleDataSource.slice(start, start + rulePageSize);
  }, [ruleCurrentPage, rulePageSize, stepRuleDataSource]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(stepRuleDataSource.length / rulePageSize));
    if (ruleCurrentPage > maxPage) {
      setRuleCurrentPage(maxPage);
    }
  }, [ruleCurrentPage, rulePageSize, stepRuleDataSource.length]);

  const loadTaskDetail = async () => {
    if (!taskId) return;
    setTaskLoading(true);
    try {
      const res = await getGoodsRecommendTaskDetailApi({ id: taskId, shopId });
      if (res?.code === 200 && res?.data) {
        const detail = res.data;
        const isScheduled = Number(detail?.status) !== 1 && detail?.startTime;
        setCurrentTaskStatus(Number(detail?.status));
        taskForm.setFieldsValue({
          taskName: detail.taskName || '',
          effectMode: isScheduled ? 'scheduled' : 'immediate',
          scheduledTime: isScheduled && detail.startTime ? dayjs(detail.startTime) : null,
          recommendOrderList: normalizeRecommendOrderList(detail.recommendOrderList),
        });
        setResolvedTaskId(detail.id ?? taskId);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setTaskLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchBoughtDict();
    fetchPetTypes();
    fetchTags(1);
    setSelectedRuleIds([]);
    setRuleModalVisible(false);
    setCurrentRuleId(null);
    setTaskStep(1);
    setRuleCurrentPage(1);
    setRulePageSize(10);
    setCurrentTaskStatus(null);
    setDraftRules([]);
    setDraftRuleQuery({});
    setPendingDeletedRuleIds([]);
    setPendingImportedRules([]);
    setRuleDetailDataSource([]);
    draftRuleSeedRef.current = 0;
    resetRuleFilters();
    if (mode === 'create') {
      setResolvedTaskId(null);
      taskForm.setFieldsValue(buildDefaultTaskValues());
      setTaskLoading(false);
      return;
    }
    setResolvedTaskId(taskId ?? null);
    taskForm.setFieldsValue(buildDefaultTaskValues());
    loadTaskDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, open, taskId, shopId, shopType]);

  useEffect(() => {
    if (!open || taskStep !== 2 || !resolvedTaskId) return;
    fetchRuleList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskStep, resolvedTaskId]);

  const fetchRuleList = async (override?: Partial<{ taskId: number | string }>) => {
    const activeTaskId = override?.taskId ?? resolvedTaskId;
    if (!activeTaskId) return;
    const values = queryForm.getFieldsValue();
    const updateTime: Dayjs[] | undefined = values.updateTime;
    const params = {
      shopId,
      taskId: activeTaskId,
      petType: values.petType || undefined,
      ageStage: values.ageStage || undefined,
      petBreed: values.petBreed || undefined,
      tag: values.tag || undefined,
      purchasedFlag:
        values.purchasedFlag === undefined || values.purchasedFlag === null || values.purchasedFlag === ''
          ? undefined
          : values.purchasedFlag,
      skuId: values.skuId || undefined,
      updateTimeBegin: updateTime?.[0] ? updateTime[0].startOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      updateTimeEnd: updateTime?.[1] ? updateTime[1].endOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      pageNum: 1,
      pageSize: 9999,
    };
    setRuleListLoading(true);
    try {
      const [res, detailRes] = await Promise.all([
        getGoodsRecommendListApi(params),
        getGoodsRecommendDetailListApi({
          shopId,
          taskId: activeTaskId,
        }),
      ]);
      if (res?.code === 200) {
        setRuleDataSource(res.rows || []);
        setRuleCurrentPage(1);
        setSelectedRuleIds([]);
      }
      if (detailRes?.code === 200) {
        setRuleDetailDataSource(Array.isArray(detailRes?.data) ? detailRes.data : []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setRuleListLoading(false);
    }
  };

  const resolveTaskIdFromSaveResult = async (taskPayload: any, responseData: any) => {
    const directId =
      responseData?.id ??
      responseData?.taskId ??
      (typeof responseData === 'number' || typeof responseData === 'string' ? responseData : undefined);
    if (directId) return directId;
    const listRes = await getGoodsRecommendTaskListApi({
      shopId,
      taskName: taskPayload.taskName,
      pageNum: 1,
      pageSize: 50,
    });
    const rows = Array.isArray(listRes?.rows) ? listRes.rows : [];
    const matchedRows = rows
      .filter(item => item.taskName === taskPayload.taskName)
      .filter(item => JSON.stringify(item.recommendOrderList || []) === JSON.stringify(taskPayload.recommendOrderList))
      .sort((a, b) => {
        const bTime = dayjs(b.updateTime || b.startTime || 0).valueOf();
        const aTime = dayjs(a.updateTime || a.startTime || 0).valueOf();
        return bTime - aTime;
      });
    return matchedRows[0]?.id;
  };

  const handleNextStep = async () => {
    if (isView) {
      setTaskStep(2);
      return;
    }
    try {
      await taskForm.validateFields();
      setTaskStep(2);
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(error);
    }
  };

  const handleSaveDraftRule = async (goodsPayload: any) => {
    const draftId =
      ruleModalMode === 'edit' && currentDraftRule?.__draftId
        ? currentDraftRule.__draftId
        : `draft-${Date.now()}-${++draftRuleSeedRef.current}`;
    const nextDraftRule = {
      ...(ruleModalMode === 'edit' && currentDraftRule ? currentDraftRule : {}),
      ...goodsPayload,
      __draftId: draftId,
      ...(currentDraftRule?.__isImport
        ? {
            __isImport: true,
            __importGroupKey: currentDraftRule.__importGroupKey ?? getImportGroupKey(goodsPayload),
            __importPayloadRows: buildImportPayloadRowsFromRule({
              ...(ruleModalMode === 'edit' && currentDraftRule ? currentDraftRule : {}),
              ...goodsPayload,
            }),
          }
        : {}),
    };
    setDraftRules(prev => {
      if (ruleModalMode === 'edit') {
        return prev.map(item => (String(item.__draftId) === String(draftId) ? nextDraftRule : item));
      }
      return [...prev, nextDraftRule];
    });
  };

  const handleSavePendingImportedRule = async (goodsPayload: any) => {
    const draftId =
      ruleModalMode === 'edit' && currentPendingImportedRule?.__draftId
        ? currentPendingImportedRule.__draftId
        : `draft-import-${Date.now()}-${++draftRuleSeedRef.current}`;
    const nextDraftRule = {
      ...(ruleModalMode === 'edit' && currentPendingImportedRule ? currentPendingImportedRule : {}),
      ...goodsPayload,
      __draftId: draftId,
      __isImport: true,
      __importGroupKey: currentPendingImportedRule?.__importGroupKey ?? getImportGroupKey(goodsPayload),
      __importPayloadRows: buildImportPayloadRowsFromRule({
        ...(ruleModalMode === 'edit' && currentPendingImportedRule ? currentPendingImportedRule : {}),
        ...goodsPayload,
      }),
    };
    setPendingImportedRules(prev =>
      prev.map(item => (String(item.__draftId) === String(draftId) ? nextDraftRule : item)),
    );
  };

  const handleSaveTaskRule = async (goodsPayload: any) => {
    if (currentPendingImportedRule) {
      await handleSavePendingImportedRule(goodsPayload);
      return;
    }
    if (mode === 'edit') {
      handleSaveLocalTaskRule(goodsPayload);
      return;
    }
    const taskPayload = await getTaskPayload();
    const res = await addOrUpdateGoodsRecommendTaskAndGoodsApi({
      task: taskPayload,
      goods: buildGoodsPayload(goodsPayload, resolvedTaskId ?? undefined, {
        importIdsOnly: Boolean(goodsPayload?.__isImport),
      }),
      ...(isImportedRule(goodsPayload) ? { isImport: true } : {}),
    });
    if (res?.code !== 200) {
      throw new Error(ruleModalMode === 'edit' ? '编辑规则失败' : '新建规则失败');
    }
    message.success(ruleModalMode === 'edit' ? '编辑规则成功' : '新建规则成功');
  };

  const handleCreateTaskWithDraftRules = async () => {
    try {
      const taskPayload = await getTaskPayload();
      if (!draftRules.length) {
        message.warning('请至少新增一条推荐规则后再创建任务');
        return;
      }
      setTaskSubmitting(true);
      const importedRules = draftRules.filter(rule => isImportedRule(rule));
      const manualRules = draftRules.filter(rule => !isImportedRule(rule));
      let nextTaskId: number | string | undefined;

      if (importedRules.length) {
        const importRes = await addOrUpdateGoodsRecommendTaskAndGoodsApi({
          task: taskPayload,
          goods: buildImportedRawGoodsPayload(importedRules),
          isImport: true,
        });
        if (importRes?.code !== 200) return;
        if (manualRules.length) {
          nextTaskId = await resolveTaskIdFromSaveResult(taskPayload, importRes?.data);
          if (!nextTaskId) {
            throw new Error('任务已创建，但未获取到任务ID，请刷新列表后重试');
          }
        }
      }

      if (manualRules.length) {
        const [firstRule, ...restRules] = manualRules;
        const firstRes = await addOrUpdateGoodsRecommendTaskAndGoodsApi({
          task: nextTaskId
            ? {
                ...taskPayload,
                id: nextTaskId,
              }
            : taskPayload,
          goods: buildGoodsPayload(firstRule, nextTaskId),
        });
        if (firstRes?.code !== 200) return;
        if (!nextTaskId) {
          nextTaskId = await resolveTaskIdFromSaveResult(taskPayload, firstRes?.data);
          if (!nextTaskId) {
            throw new Error('任务已创建，但未获取到任务ID，请刷新列表后重试');
          }
        }
        const savedTaskPayload = {
          ...taskPayload,
          id: nextTaskId,
        };
        for (const rule of restRules) {
          const ruleRes = await addOrUpdateGoodsRecommendTaskAndGoodsApi({
            task: savedTaskPayload,
            goods: buildGoodsPayload(rule, nextTaskId),
          });
          if (ruleRes?.code !== 200) {
            throw new Error('部分推荐规则保存失败，请刷新后检查');
          }
        }
      }
      message.success('任务创建成功');
      onSuccess();
    } catch (error: any) {
      if (error?.errorFields) return;
      if (error instanceof Error && error.message) {
        message.error(error.message);
        return;
      }
      console.error(error);
    } finally {
      setTaskSubmitting(false);
    }
  };

  const pollImportResult = async (importStartTime: number | string) => {
    for (let index = 0; index < IMPORT_RESULT_POLL_TIMES; index += 1) {
      const res = await getGoodsRecommendImportResultApi({
        shopId,
        importStartTime,
      });
      const importedRows = Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.list) ? res.data.list : [];
      if (importedRows.length) {
        return importedRows;
      }
      await new Promise(resolve => {
        window.setTimeout(resolve, IMPORT_RESULT_POLL_INTERVAL);
      });
    }
    return [];
  };

  const handleOpenRuleModal = (nextMode: 'create' | 'edit' | 'view', record?: any) => {
    if (!resolvedTaskId && !isCreateDraftMode && nextMode !== 'create') {
      message.warning('请先保存任务');
      return;
    }
    setRuleModalMode(nextMode);
    setCurrentRuleId(record?.__draftId ?? record?.__localRuleId ?? record?.id ?? null);
    setRuleModalVisible(true);
  };

  const handleDeleteRule = (record: any) => {
    Modal.confirm({
      title: '确认删除该条推荐规则？',
      okText: '删除',
      okButtonProps: { danger: true },
      onOk: async () => {
        if (isCreateDraftMode) {
          const draftId = record.__draftId ?? record.id;
          setDraftRules(prev => prev.filter(item => String(item.__draftId) !== String(draftId)));
          setSelectedRuleIds([]);
          message.success('删除成功');
          return;
        }
        if (record.__draftId) {
          setPendingImportedRules(prev => prev.filter(item => String(item.__draftId) !== String(record.__draftId)));
          setSelectedRuleIds(prev => prev.filter(id => String(id) !== String(record.__rowId ?? record.id)));
          message.success('删除成功');
          return;
        }
        if (record.__localRuleId) {
          setRuleDetailDataSource(prev =>
            prev.filter(item => String(item.__localRuleId) !== String(record.__localRuleId)),
          );
          setRuleDataSource(prev => prev.filter(item => String(item.__localRuleId) !== String(record.__localRuleId)));
          setSelectedRuleIds(prev => prev.filter(id => String(id) !== String(record.__rowId ?? record.id)));
          message.success('删除成功');
          return;
        }
        setPendingDeletedRuleIds(prev => (prev.includes(record.id) ? prev : [...prev, record.id]));
        setSelectedRuleIds(prev => prev.filter(id => String(id) !== String(record.id)));
        message.success('删除成功');
      },
    });
  };

  const handleBatchDelete = () => {
    if (!selectedRuleIds.length) {
      message.warning('请选择要删除的推荐规则');
      return;
    }
    Modal.confirm({
      title: `确认删除选中的${selectedRuleIds.length}条推荐规则？`,
      okText: '删除',
      okButtonProps: { danger: true },
      onOk: async () => {
        if (isCreateDraftMode) {
          const draftIds = Array.from(
            new Set(
              draftRuleRows.filter(item => selectedRuleIds.includes(item.__rowId)).map(item => String(item.__draftId)),
            ),
          );
          setDraftRules(prev => prev.filter(item => !draftIds.includes(String(item.__draftId))));
          setSelectedRuleIds([]);
          message.success('删除成功');
          return;
        }
        const localDraftIds = Array.from(
          new Set(
            pendingImportedRuleRows
              .filter(item => selectedRuleIds.includes(item.__rowId))
              .map(item => String(item.__draftId)),
          ),
        );
        const selectedLocalManualRows = ruleDataSource.filter(item => {
          const rowKey = item.__rowId ?? item.id;
          return item.__localRuleId && selectedRuleIds.some(id => String(id) === String(rowKey));
        });
        const localManualRuleIds = Array.from(new Set(selectedLocalManualRows.map(item => String(item.__localRuleId))));
        const localManualRowIds = new Set(selectedLocalManualRows.map(item => String(item.__rowId ?? item.id)));
        const remoteIds = selectedRuleIds.filter(
          id => !String(id).includes('draft-import-') && !localManualRowIds.has(String(id)),
        );
        if (localDraftIds.length) {
          setPendingImportedRules(prev => prev.filter(item => !localDraftIds.includes(String(item.__draftId))));
        }
        if (remoteIds.length) {
          setPendingDeletedRuleIds(prev => Array.from(new Set([...prev, ...remoteIds])));
        }
        setRuleDetailDataSource(prev =>
          prev.filter(rule => {
            const localRuleId = rule?.__localRuleId;
            return !localRuleId || !localManualRuleIds.includes(String(localRuleId));
          }),
        );
        setRuleDataSource(prev =>
          prev.filter(row => {
            const localRuleId = row?.__localRuleId;
            return !localRuleId || !localManualRuleIds.includes(String(localRuleId));
          }),
        );
        setSelectedRuleIds([]);
        message.success('删除成功');
      },
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadGoodsRecommendTemplateApi(platformType);
    } catch (error) {
      console.error(error);
    }
  };

  const handleExportData = async () => {
    if (!resolvedTaskId) {
      message.warning('请先保存任务');
      return;
    }
    const values = queryForm.getFieldsValue();
    const updateTime: Dayjs[] | undefined = values.updateTime;
    try {
      await exportGoodsRecommendDataApi({
        platform: platformType,
        data: {
          shopId,
          taskId: resolvedTaskId,
          petType: values.petType || undefined,
          ageStage: values.ageStage || undefined,
          petBreed: values.petBreed || undefined,
          tag: values.tag || undefined,
          purchasedFlag:
            values.purchasedFlag === undefined || values.purchasedFlag === null || values.purchasedFlag === ''
              ? undefined
              : values.purchasedFlag,
          skuId: values.skuId || undefined,
          updateTimeBegin: updateTime?.[0] ? updateTime[0].startOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
          updateTimeEnd: updateTime?.[1] ? updateTime[1].endOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportLoading(true);
    try {
      const res = await importGoodsRecommendDataApi({
        shopId,
        taskId: mode === 'edit' ? resolvedTaskId : undefined,
        platform: platformType,
        file,
      });
      if (res?.code === 200) {
        const directRows = Array.isArray(res?.data?.successList)
          ? res.data.successList
          : Array.isArray(res?.data?.list)
            ? res.data.list
            : [];
        const importStartTime = res?.data?.importStartTime;
        const polledRows = importStartTime ? await pollImportResult(importStartTime) : [];
        const importedRows = polledRows.length ? polledRows : directRows;
        if (!importedRows.length) {
          throw new Error('导入处理中，请稍后重试');
        }
        const importedDraftRules = await mapImportedRowsToDraftRules(importedRows);
        if (isCreateDraftMode) {
          resetRuleFilters();
          setRuleCurrentPage(1);
          setDraftRuleQuery({});
          setDraftRules(prev => [...importedDraftRules, ...prev]);
        } else if (mode === 'edit') {
          resetRuleFilters();
          setRuleCurrentPage(1);
          setPendingImportedRules(prev => [...importedDraftRules, ...prev]);
        }
        message.success(res?.msg || '导入成功');
      }
    } catch (error: any) {
      if (error?.errorFields) return;
      if (error instanceof Error && error.message) {
        message.error(error.message);
        return;
      }
      console.error(error);
    } finally {
      setImportLoading(false);
    }
  };

  const ruleColumns: ColumnsType<any> = [
    { title: '类型', dataIndex: 'petType', width: 120 },
    { title: '年龄段', dataIndex: 'ageStage', width: 140 },
    { title: '品种', dataIndex: 'petBreed', width: 140 },
    { title: '标签', dataIndex: 'tag', width: 120 },
    {
      title: '是否已购',
      dataIndex: 'purchasedFlag',
      width: 120,
      render: value => boughtDict.find(item => String(item.code) === String(value))?.name || '-',
    },
    { title: 'SKU', dataIndex: 'skuId', width: 160 },
    { title: '商品名称', dataIndex: 'productName', width: 180 },
    { title: '卖点', dataIndex: 'sellingPoint', width: 220 },
    { title: '优先级权重', dataIndex: 'priorityWeight', width: 120 },
    {
      title: '操作',
      width: isView ? 100 : 140,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          {isView ? (
            <Button
              type="link"
              onClick={() => handleOpenRuleModal('view', record)}>
              查看
            </Button>
          ) : (
            <>
              {checkPermissions(GOODS_RECOMMEND_KEY.UPDATE) && (
                <Button
                  type="link"
                  onClick={() => handleOpenRuleModal('edit', record)}>
                  编辑
                </Button>
              )}
              {checkPermissions(GOODS_RECOMMEND_KEY.DELETE) && (
                <Button
                  type="link"
                  danger
                  onClick={() => handleDeleteRule(record)}>
                  删除
                </Button>
              )}
            </>
          )}
        </Space>
      ),
    },
  ];

  const renderTaskStep = () => (
    <Spin spinning={taskLoading}>
      <Form
        form={taskForm}
        layout="horizontal"
        labelCol={{ flex: '120px' }}
        wrapperCol={{ flex: '1' }}
        labelAlign="right"
        initialValues={buildDefaultTaskValues()}>
        <Form.Item
          label="任务名称"
          name="taskName"
          rules={[
            { required: true, message: '请输入任务名称' },
            { max: 50, message: '任务名称不超过50个字符' },
          ]}>
          <Input
            placeholder="请输入"
            maxLength={50}
            showCount
            disabled={isView}
            style={{ maxWidth: 560 }}
          />
        </Form.Item>

        <Form.Item
          label="任务生效时间"
          required>
          <Form.Item
            name="effectMode"
            style={{ marginBottom: 0 }}>
            <Radio.Group
              disabled={disableTaskEffectTime}
              onChange={e => {
                if (e.target.value === 'immediate') {
                  taskForm.setFieldValue('scheduledTime', null);
                }
              }}>
              <Space
                direction="vertical"
                size={16}>
                <Radio value="immediate">立即生效</Radio>
                <Space size={12}>
                  <Radio value="scheduled">定时生效</Radio>
                  <Form.Item
                    shouldUpdate={(prev, cur) => prev.effectMode !== cur.effectMode}
                    noStyle>
                    {() => {
                      const effectMode = taskForm.getFieldValue('effectMode');
                      return (
                        <Form.Item
                          name="scheduledTime"
                          style={{ marginBottom: 0 }}
                          rules={
                            effectMode === 'scheduled' ? [{ required: true, message: '请选择生效时间' }] : undefined
                          }>
                          <DatePicker
                            showTime
                            allowClear
                            placeholder="请选择生效时间"
                            disabled={disableTaskEffectTime || effectMode !== 'scheduled'}
                            style={{ width: 240 }}
                            format="YYYY-MM-DD HH:mm:ss"
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Space>
              </Space>
            </Radio.Group>
          </Form.Item>
        </Form.Item>

        <Form.Item
          label="商品推荐逻辑"
          required
          style={{ marginBottom: 0 }}>
          <div style={{ maxWidth: 420 }}>
            {logicOptions.map((item, index) => (
              <Form.Item
                key={item.value}
                label={`第${index + 1}优先级`}
                style={{ marginBottom: index === logicOptions.length - 1 ? 0 : 16 }}>
                <Form.Item
                  name={['recommendOrderList', index]}
                  noStyle
                  rules={[{ required: true, message: '请选择商品推荐逻辑' }]}>
                  <Select
                    placeholder="请选择"
                    disabled={isView}
                    allowClear
                    style={{ width: 240 }}
                    options={logicOptions.map(option => ({
                      ...option,
                      disabled: selectedLogicValues.some(
                        (selectedValue: string, selectedIndex: number) =>
                          selectedIndex !== index && selectedValue === option.value,
                      ),
                    }))}
                  />
                </Form.Item>
              </Form.Item>
            ))}
          </div>
        </Form.Item>
      </Form>
    </Spin>
  );

  const renderRuleStep = () => (
    <>
      <Form
        form={queryForm}
        layout="inline"
        preserve={false}
        style={{ marginBottom: 16, rowGap: 12 }}>
        <Form.Item label="类型月龄">
          <Space>
            <Form.Item
              name="petType"
              noStyle>
              <Select
                placeholder="类型"
                style={{ width: 160 }}
                allowClear
                options={petTypeOptions}
                showSearch
                optionFilterProp="label"
                onChange={value => {
                  queryForm.setFieldsValue({ petType: value, ageStage: undefined, petBreed: undefined });
                  fetchAgeStages(value);
                  fetchBreeds(value);
                }}
              />
            </Form.Item>
            <Form.Item
              name="ageStage"
              noStyle>
              <Select
                placeholder="全部月龄"
                style={{ width: 180 }}
                allowClear
                loading={ageStageLoading}
                options={filterAgeStageOptions}
                showSearch
                optionFilterProp="label"
                onFocus={() => fetchAgeStages(queryForm.getFieldValue('petType'))}
              />
            </Form.Item>
          </Space>
        </Form.Item>
        <Form.Item
          label="品种"
          name="petBreed">
          <Select
            placeholder="全部"
            style={{ width: 200 }}
            allowClear
            loading={breedLoading}
            options={filterBreedOptions}
            showSearch
            optionFilterProp="label"
            onFocus={() => fetchBreeds(queryForm.getFieldValue('petType'))}
          />
        </Form.Item>
        <Form.Item
          label="标签"
          name="tag">
          <Select
            placeholder="全部"
            style={{ width: 200 }}
            allowClear
            loading={tagLoading}
            options={tagOptions}
            showSearch
            optionFilterProp="label"
            onOpenChange={nextOpen => {
              if (nextOpen && !tagOptions.length) {
                fetchTags(1);
              }
            }}
            onPopupScroll={e => {
              const target = e.target as HTMLDivElement;
              if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 20) {
                const hasMore = tagOptions.length < tagTotal;
                if (!tagLoading && hasMore) {
                  fetchTags(tagPageNum + 1);
                }
              }
            }}
          />
        </Form.Item>
        <Form.Item
          label="是否已购"
          name="purchasedFlag">
          <Select
            placeholder="全部"
            style={{ width: 180 }}
            options={[
              { label: '全部', value: '' },
              ...boughtDict.map(item => ({ label: item.name, value: item.code })),
            ]}
          />
        </Form.Item>
        <Form.Item
          label="SKU"
          name="skuId">
          <Input
            placeholder="请输入"
            allowClear
            style={{ width: 220 }}
          />
        </Form.Item>
        <Form.Item
          label="更新时间"
          name="updateTime">
          <RangePicker
            showTime={{
              defaultValue: [dayjs('00:00:00', 'HH:mm:ss'), dayjs('23:59:59', 'HH:mm:ss')],
            }}
            style={{ width: 400 }}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => {
                if (isCreateDraftMode) {
                  setRuleCurrentPage(1);
                  setDraftRuleQuery(queryForm.getFieldsValue());
                  return;
                }
                fetchRuleList();
              }}>
              查询
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                resetRuleFilters();
                if (isCreateDraftMode) {
                  setRuleCurrentPage(1);
                  setDraftRuleQuery({});
                  return;
                }
                fetchRuleList();
              }}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>
      {!isView && (
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            {checkPermissions(GOODS_RECOMMEND_KEY.ADD) && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleOpenRuleModal('create')}>
                新建规则
              </Button>
            )}
            {checkPermissions(GOODS_RECOMMEND_KEY.IMPORT) && (
              <Button
                icon={<ImportOutlined />}
                loading={importLoading}
                onClick={handleImportClick}>
                导入
              </Button>
            )}
            {checkPermissions(GOODS_RECOMMEND_KEY.TEMPLATE) && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadTemplate}>
                下载模板
              </Button>
            )}
            {checkPermissions(GOODS_RECOMMEND_KEY.LIST) && (
              <Button
                icon={<DownloadOutlined />}
                disabled={isCreateDraftMode}
                onClick={handleExportData}>
                导出数据
              </Button>
            )}
            {checkPermissions(GOODS_RECOMMEND_KEY.DELETE) && (
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={!selectedRuleIds.length}
                onClick={handleBatchDelete}>
                删除
              </Button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx"
              style={{ display: 'none' }}
              onChange={handleImportChange}
            />
          </Space>
        </div>
      )}
      <Table
        rowKey={record => record.__rowId ?? record.id}
        loading={ruleListLoading}
        columns={ruleColumns}
        dataSource={pagedRuleDataSource}
        rowSelection={
          isView
            ? undefined
            : {
                selectedRowKeys: selectedRuleIds,
                onChange: keys => setSelectedRuleIds(keys as Array<number | string>),
              }
        }
        pagination={{
          current: ruleCurrentPage,
          pageSize: rulePageSize,
          total: stepRuleDataSource.length,
          showSizeChanger: true,
          showTotal: total => `共 ${total} 条`,
          onChange: (page, size) => {
            setRuleCurrentPage(page);
            if (size !== rulePageSize) {
              setRulePageSize(size);
            }
          },
        }}
        scroll={{ x: 1400 }}
      />
    </>
  );

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
      <Button onClick={onClose}>取消</Button>
      {taskStep === 1 ? (
        <Button
          type="primary"
          loading={taskSubmitting}
          disabled={taskLoading}
          onClick={handleNextStep}>
          {isView ? '查看规则' : '下一步'}
        </Button>
      ) : (
        <>
          <Button onClick={() => setTaskStep(1)}>上一步</Button>
          <Button
            type="primary"
            loading={taskSubmitting}
            onClick={async () => {
              if (isView) {
                onSuccess();
                return;
              }
              if (mode === 'create') {
                await handleCreateTaskWithDraftRules();
                return;
              }
              try {
                setTaskSubmitting(true);
                const payload = await getTaskPayload();
                const existingRuleDetails = buildRuleDetailGoodsPayload(ruleDetailDataSource, pendingDeletedRuleIds);
                const importedGoods = buildImportedRawGoodsPayload(pendingImportedRules);
                const finalGoods = pendingImportedRules.length
                  ? [...importedGoods, ...existingRuleDetails]
                  : existingRuleDetails;
                const res = await addOrUpdateGoodsRecommendTaskAndGoodsApi({
                  task: payload,
                  goods: finalGoods,
                  ...(pendingImportedRules.length ? { isImport: true } : {}),
                });
                if (res?.code === 200) {
                  if (pendingDeletedRuleIds.length) {
                    const deleteRes = await batchDeleteGoodsRecommendApi({ shopId, ids: pendingDeletedRuleIds });
                    if (deleteRes?.code !== 200) return;
                  }
                  message.success('任务保存成功');
                  onSuccess();
                }
              } catch (error: any) {
                if (error?.errorFields) return;
                console.error(error);
              } finally {
                setTaskSubmitting(false);
              }
            }}>
            {mode === 'create' ? '保存并创建' : isView ? '关闭' : '完成'}
          </Button>
        </>
      )}
    </div>
  );

  return (
    <>
      <Drawer
        open={open}
        title={drawerTitle}
        width={taskStep === 1 ? 760 : '88vw'}
        destroyOnClose
        onClose={onClose}
        footer={footer}>
        {taskStep === 1 ? renderTaskStep() : renderRuleStep()}
      </Drawer>
      {ruleModalVisible ? (
        <RuleEditorModal
          open={ruleModalVisible}
          mode={ruleModalMode}
          shopId={shopId}
          taskId={resolvedTaskId ?? undefined}
          ruleId={isCreateDraftMode ? undefined : currentRuleId}
          platformType={platformType}
          initialData={isCreateDraftMode ? currentDraftRule : (currentPendingImportedRule ?? currentLocalManualRule)}
          onSubmit={isCreateDraftMode ? handleSaveDraftRule : handleSaveTaskRule}
          onCancel={() => {
            setRuleModalVisible(false);
            setCurrentRuleId(null);
          }}
          onSuccess={() => {
            setRuleModalVisible(false);
            setCurrentRuleId(null);
            if (!isCreateDraftMode && mode !== 'edit' && !currentPendingImportedRule) {
              fetchRuleList();
            }
          }}
        />
      ) : null}
    </>
  );
};

export default TaskDrawer;
